import { computed, toValue, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useStore } from 'vuex'
import { useClientStore } from '@shared/stores/client.js'
import { getSectionRouteLocation } from '@shared/utils/route.js'

export default function useCollection (collection) {
  const { isAuthenticated } = storeToRefs(useClientStore())

  const name = computed(() => toValue(collection)?.name)
  const itemCountText = computed(() => {
    const count = toValue(collection)?.count
    return typeof count === 'number' ? `${count} item${count === 1 ? '' : 's'}` : null
  })
  const previewImageUrls = computed(() => {
    const collectionValue = toValue(collection)
    if (!collectionValue) return []

    // For Armoire collections, use bannerImageUrl as cover image if available
    if (isArmoireCollection(collectionValue) && collectionValue.bannerImageUrl) {
      return [collectionValue.bannerImageUrl]
    }

    // Fall back to preview images for all collections (member collections or Armoire collections without cover image)
    return collectionValue.previewImages ?? []
  })
  const sourceType = computed(() => toValue(collection)?.sourceType)
  const routeLocation = computed(() => {
    const id = toValue(collection)?.id
    if (sourceType.value === 'section') {
      return getSectionRouteLocation({ id, name: name.value }, isAuthenticated.value)
    }
    return id ? { name: 'collection', params: { id } } : { path: '#' }
  })

  return {
    name,
    itemCountText,
    previewImageUrls,
    sourceType,
    routeLocation
  }
}

export function useCollectionById (id) {
  const store = useStore()
  const collection = computed(() => store.state.collections.collectionsById[toValue(id)])

  watch(id, () => {
    if (!collection.value || (toValue(id) && collection.value.id !== toValue(id))) {
      store.dispatch('collections/getCollection', { collectionId: toValue(id) })
    }
  }, { immediate: true })

  return useCollection(collection)
}

// Determines if a collection is an Armoire collection (StyleColorSection)
export function isArmoireCollection(collection) {
  return collection?.sourceType === 'section' ||
         (collection?.client && collection?.client.firstName === 'Armoire')
}
